import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import axios from "axios";
import { getClassWiseSubjects } from "../../../services/SubjectServices";
import { getProgramWiseExamList } from "../../../services/ExamService";
import { getScoreSchemeByEvaluationType } from "../../../services/ScoreSchemeService";
import {
  getExamConfigById,
  createExamConfig,
  updateExamConfig,
} from "../../../services/ExamConfigService";

export const useExamConfigManage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [scoreSchemesData, setScoreSchemesData] = useState([]);
  const [program_id, setProgramId] = useState("");

  /** SINGLE SOURCE OF TRUTH */
  const [state, setState] = useState({
    loader: false,
    student_loader: false,
    id: null,
    name: "",
    program_id: "",
    subject_id: "",
    term: "",
    description: "",
    start: "",
    end: "",
    evaluation_type_id: "",
    grade_method: "",
    attempts: "NO",
    total_attempts: "",
    is_active: 1,
    is_traits: false,
    maxScore: "",
    traits: [{ trait_id: "", trait_grade_scheme_type: "" }],
    studentList: [],
    subjectData: [],
    examList: [],
    scoreSchemesData: [],
    gradingMethodsData: [],
    freezed: false,
  });

  const [student_ids, setStudentIds] = useState([]);
  const [studentSubjectWise, setStudentSubjectWise] = useState(false);
  const [gradeSchemeID, setGradeSchemeID] = useState("");
  const [grade_scheme_type, setGradeSchemeType] = useState("");

  /** Fetch subjects */
  const getSubject = useCallback(async (programId) => {
    try {
      const res = await getClassWiseSubjects(programId);
      const subjectData = res.data.data;
      setState((prev) => ({ ...prev, subjectData }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  /** Fetch exams */
  const getExamList = useCallback(async (programId) => {
    setState((prev) => ({ ...prev, student_loader: true }));

    try {
      const res = await getProgramWiseExamList(programId);
      if (res.status === 200) {
        setState((prev) => ({ ...prev, examList: res.data.data }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setState((prev) => ({ ...prev, student_loader: false }));
    }
  }, []);

  /** Fetch score schemes */
  const fetchScoreSchemesByEvaluation = useCallback(
    async (evaluationTypeId) => {
      try {
        const res = await getScoreSchemeByEvaluationType(evaluationTypeId);
        if (res.status === 200) {
          setState((prev) => ({ ...prev, scoreSchemesData: res.data.data }));
          setScoreSchemesData(res.data.data);
        }
      } catch (error) {
        console.error(error);
      }
    },
    []
  );

  /** Fetch existing record (edit mode) */
  const getUniqueEvent = useCallback(async () => {
    if (!params.id) return;

    setState((prev) => ({ ...prev, loader: true }));

    try {
      const response = await getExamConfigById(params.id);

      if (response.status === 200) {
        const data = response.data.data;
        const {
          program,
          subject,
          evaluation_type,
          grade_scheme,
          traits,
          max_score,
          ...rest
        } = data;

        setGradeSchemeID(grade_scheme.id);
        setGradeSchemeType(grade_scheme.type);
        setProgramId(program.id);

        setState((prev) => ({
          ...prev,
          ...rest,
          program_id: program.id,
          subject_id: subject.id,
          name: data.name.id,
          term: data.term,
          start: data.start,
          end: data.end,
          grade_scheme_id: grade_scheme.id,
          grade_method: data.grade_method,
          evaluation_type_id: evaluation_type.id,
          maxScore: max_score,
          traits: traits.map((t) => ({
            trait_grade_scheme_type: t.grade_scheme.type,
            trait_id: t.subject_trait.id,
            label: t.subject_trait.name,
            value: t.subject_trait.id,
            grade_scheme_id: grade_scheme.id,
          })),
        }));

        /** Trigger dependent dropdowns */
        getExamList(program.id);
        getSubject(program.id);
        fetchScoreSchemesByEvaluation(evaluation_type.id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setState((prev) => ({ ...prev, loader: false }));
    }
  }, [params.id, getExamList, getSubject, fetchScoreSchemesByEvaluation]);

  /** Fetch Grade Methods */
  const fetchGradingMethods = useCallback(async () => {
    try {
      const res = await axios.get("/grade/methods");
      if (res.status === 200) {
        setState((prev) => ({
          ...prev,
          gradingMethodsData: res.data.data,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  /** Init */
  useEffect(() => {
    getUniqueEvent();
    fetchGradingMethods();
  }, [getUniqueEvent, fetchGradingMethods]);

  const handleChange = useCallback(
    (e) => {
      const { name, value, checked } = e.target;

      // Toggle traits
      if (name === "is_traits") {
        setState((prev) => ({ ...prev, is_traits: checked }));
        return;
      }

      // Change grade scheme
      if (name === "gradeSchemeID") {
        const selectedItem = scoreSchemesData.find(
          (item) => Number(item.id) === Number(value)
        );

        setGradeSchemeID(value);

        if (selectedItem) {
          setGradeSchemeType(selectedItem.type);
        }
        return;
      }

      // Change evaluation type → load score schemes
      if (name === "evaluation_type_id") {
        setState((prev) => ({ ...prev, evaluation_type_id: value }));
        fetchScoreSchemesByEvaluation(value);
        return;
      }

      // Change program → load students, exam, subject
      if (name === "program_id") {
        setProgramId(value);
        getExamList(value);
        getSubject(value);
        setStudentIds([]);
        return;
      }

      // Status toggle
      if (name === "is_active") {
        setState((prev) => ({ ...prev, is_active: checked ? 1 : 0 }));
        return;
      }

      // Default input update
      setState((prev) => ({ ...prev, [name]: value }));
    },
    [scoreSchemesData, fetchScoreSchemesByEvaluation, getExamList, getSubject]
  );

  /** SUBMIT */
  const handleSubmit = async (event) => {
    event.preventDefault();
    const pathname = window.location.pathname;
    const clone = pathname.includes("clone");
    setState((prevState) => ({
      ...prevState,
      loader: true,
      attempts: "NO",
      total_attempts: "",
    }));
    const session_id = localStorage.getItem("session_id");
    const grade_scheme_id = gradeSchemeID;
    let max_score = 0;
    if (grade_scheme_type === "Score") max_score = state.maxScore;
    let traits;

    if (!state.is_traits) {
      traits = [];
    } else {
      traits = state.traits.map((item) => {
        return {
          trait_id: item.trait_id,
          grade_scheme_id: item.grade_scheme_id,
        };
      });
    }

    const {
      name,
      term,
      start,
      end,
      evaluation_type_id,
      attempts,
      total_attempts,
      grade_method,
      description,
      is_traits,
      is_active,
    } = state;

    const data = {
      name,
      term,
      session_id,
      start,
      end,
      program_id,
      evaluation_type_id,
      grade_scheme_id,
      attempts,
      total_attempts,
      grade_method,
      description,
      max_score,
      student_ids,
      is_traits,
      traits,
      is_active,
    };

    try {
      if (params.id) {
        data.subject_id = state.subject_id;

        let response;
        if (clone) {
          delete data.id;
          data.subject_ids = [data.subject_id];
          response = await createExamConfig({ data });
        } else {
          response = await updateExamConfig(params.id, { data });
        }

        if (response.status === 200) {
          let msg = response.data.message.success;
          enqueueSnackbar(msg, { variant: "success" });
          navigate("/exam/config/list");
        } else {
          enqueueSnackbar(response.data.message.error, { variant: "error" });
        }
        setState((prevState) => ({ ...prevState, loader: false }));
      } else {
        data.subject_ids =
          state.subject_ids && state.subject_ids.map((item) => item.id);
        const response = await createExamConfig({ data });
        setState((prevState) => ({ ...prevState, loader: false }));
        if (response.status === 200) {
          enqueueSnackbar(response.data.message.success, {
            variant: "success",
          });
          if (!state.freezed) {
            navigate("/exam/config/list");
          }
        }
      }
    } catch (error) {
      setState((prevState) => ({ ...prevState, loader: false }));
      console.error(error);
      enqueueSnackbar("Something went wrong!", { variant: "error" });
    } finally {
      setState((prevState) => ({ ...prevState, loader: false }));
    }
  };

  const handleChangeTraits = useCallback(
    (event, index) => {
      const { name, value, label } = event.target;
      setState((prev) => {
        const updatedTraits = [...prev.traits];
        updatedTraits[index][name] = value;
        updatedTraits[index]["label"] = label;
        updatedTraits[index]["grade_scheme_id"] = gradeSchemeID;
        updatedTraits[index]["trait_grade_scheme_type"] = grade_scheme_type;
        return { ...prev, traits: updatedTraits };
      });
    },
    [gradeSchemeID, grade_scheme_type]
  );

  const handleChangeTraitsScheme = useCallback(
    (event, index) => {
      const { name, value } = event.target;
      setState((prev) => {
        const updatedTraits = [...prev.traits];
        if (name === "grade_scheme_id") {
          scoreSchemesData.forEach((item) => {
            if (item.id === value) {
              updatedTraits[index]["grade_scheme_id"] = item.id;
              updatedTraits[index]["trait_grade_scheme_type"] = item.type;
            }
          });
        } else {
          updatedTraits[index][name] = value;
        }
        return { ...prev, traits: updatedTraits };
      });
    },
    [scoreSchemesData]
  );

  const handleInputChange = (event) => {
    const { value, checked } = event.target;
    const student_id = value;
    if (checked) {
      setStudentIds([...student_ids, student_id]);
    } else {
      const updatedStudentIds = student_ids.filter((id) => id !== student_id);
      setStudentIds(updatedStudentIds);
    }
  };

  const selectAllStudent = useCallback(
    (event) => {
      const student_ids = [];
      if (event.target.checked) {
        state.studentList.forEach((student) => {
          const id = student.id.toString();
          document.getElementById(id).checked = true;
          student_ids.push(id);
        });
      } else {
        state.studentList.forEach((student) => {
          const id = student.id;
          document.getElementById(id).checked = false;
        });
      }
      setStudentIds(student_ids);
    },
    [state.studentList, setStudentIds]
  );

  const addNewRow = () => {
    setState((prevState) => ({
      ...prevState,
      traits: [...prevState.traits, {}],
    }));
  };

  const clickOnDelete = (e, record) => {
    e.preventDefault();
    e.stopPropagation();
    setState((prevState) => ({
      ...prevState,
      traits: prevState.traits.filter((r) => r !== record),
    }));
  };

  const onSubjectSelect = (event) => {
    setState((prevState) => ({
      ...prevState,
      subject_ids: event,
      selectedSubjects: event,
    }));
  };

  const onSubjectRemove = (event) => {
    setState((prevState) => ({
      ...prevState,
      subject_ids: event,
      selectedSubjects: event,
    }));
  };

  const changeStudentSubjectWise = (event) => {
    setState((prevState) => ({
      ...prevState,
      student_loader: true,
      studentList: [],
    }));
    if (event.target.checked) {
      setStudentSubjectWise(event.target.checked);
    } else {
      setStudentSubjectWise(event.target.checked);
    }
  };

  /** RETURN HOOK DATA */
  return {
    state,
    student_ids,
    setStudentIds,
    studentSubjectWise,
    setStudentSubjectWise,
    gradeSchemeID,
    grade_scheme_type,
    getExamList,
    getSubject,
    handleChange,
    handleChangeTraits,
    handleChangeTraitsScheme,
    handleInputChange,
    fetchScoreSchemesByEvaluation,
    handleSubmit,
    setState,

    program_id,
    scoreSchemesData,

    selectAllStudent,
    addNewRow,
    clickOnDelete,
    onSubjectSelect,
    onSubjectRemove,
    changeStudentSubjectWise,
  };
};
